import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Users, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getAllDestinatari, createDestinatario, updateDestinatario, deleteDestinatario } from "@/services/localDatabase";

const Destinatari = () => {
  const [destinatari, setDestinatari] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    tipo: "depurazione",
    numero_riconoscimento: "",
    nome: "",
    via: "",
    comune: "",
    provincia: "",
    partita_iva: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const data = await getAllDestinatari();
      setDestinatari(data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditing(item);
      setFormData({
        tipo: item.tipo || "depurazione",
        numero_riconoscimento: item.numero_riconoscimento || "",
        nome: item.nome || "",
        via: item.via || "",
        comune: item.comune || "",
        provincia: item.provincia || "",
        partita_iva: item.partita_iva || ""
      });
    } else {
      setEditing(null);
      setFormData({
        tipo: "depurazione",
        numero_riconoscimento: "",
        nome: "",
        via: "",
        comune: "",
        provincia: "",
        partita_iva: ""
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }
    try {
      if (editing) {
        await updateDestinatario(editing.id, formData);
        toast.success("Destinatario aggiornato");
      } else {
        await createDestinatario(formData);
        toast.success("Destinatario aggiunto");
      }
      fetchData();
      handleCloseDialog();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminare "${item.nome}"?`)) return;
    try {
      await deleteDestinatario(item.id);
      toast.success("Destinatario eliminato");
      fetchData();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore nell'eliminazione");
    }
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      depurazione: "Centro Depurazione",
      spedizione: "Centro Spedizione",
      trasformazione: "Centro Trasformazione",
      operatore_intermedio: "Operatore Intermedio",
      zona_produzione: "Zona di Produzione"
    };
    return labels[tipo] || tipo;
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 fade-in" data-testid="destinatari-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-barlow tracking-tight">
            Archivio Destinatari
          </h1>
          <p className="text-slate-500 mt-1">Gestisci i destinatari dei molluschi</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-orange-500 hover:bg-orange-600" data-testid="add-destinatario-btn">
          <Plus className="w-4 h-4 mr-2" />
          Aggiungi
        </Button>
      </div>

      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-barlow">
            <Users className="w-5 h-5 text-orange-500" />
            Elenco Destinatari ({destinatari.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-500 text-center py-8">Caricamento...</p>
          ) : destinatari.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Nessun destinatario</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>N. Ric.</TableHead>
                  <TableHead>Indirizzo</TableHead>
                  <TableHead className="w-[100px]">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {destinatari.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-xs">{getTipoLabel(item.tipo)}</TableCell>
                    <TableCell className="font-medium">{item.nome}</TableCell>
                    <TableCell>{item.numero_riconoscimento || "-"}</TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {item.via}, {item.comune} ({item.provincia})
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(item)}>
                          <Edit2 className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(item)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica Destinatario" : "Nuovo Destinatario"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Tipo *</Label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData(p => ({ ...p, tipo: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="depurazione">Centro Depurazione</SelectItem>
                    <SelectItem value="spedizione">Centro Spedizione</SelectItem>
                    <SelectItem value="trasformazione">Centro Trasformazione</SelectItem>
                    <SelectItem value="operatore_intermedio">Operatore Intermedio</SelectItem>
                    <SelectItem value="zona_produzione">Zona di Produzione</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>N. Riconoscimento</Label>
                <Input value={formData.numero_riconoscimento} onChange={(e) => setFormData(p => ({ ...p, numero_riconoscimento: e.target.value.toUpperCase() }))} className="mt-1" />
              </div>
              <div>
                <Label>Nome *</Label>
                <Input value={formData.nome} onChange={(e) => setFormData(p => ({ ...p, nome: e.target.value.toUpperCase() }))} className="mt-1" />
              </div>
              <div>
                <Label>Partita IVA</Label>
                <Input value={formData.partita_iva} onChange={(e) => setFormData(p => ({ ...p, partita_iva: e.target.value.toUpperCase() }))} placeholder="IT12345678901" className="mt-1" />
              </div>
              <div>
                <Label>Via</Label>
                <Input value={formData.via} onChange={(e) => setFormData(p => ({ ...p, via: e.target.value }))} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Comune</Label>
                  <Input value={formData.comune} onChange={(e) => setFormData(p => ({ ...p, comune: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label>Prov.</Label>
                  <Input value={formData.provincia} onChange={(e) => setFormData(p => ({ ...p, provincia: e.target.value.toUpperCase() }))} maxLength={2} className="mt-1" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}><X className="w-4 h-4 mr-2" />Annulla</Button>
              <Button type="submit" className="bg-orange-500 hover:bg-orange-600"><Save className="w-4 h-4 mr-2" />Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Destinatari;
